import React, {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
} from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { ActivityIndicator, LayoutAnimation, Platform } from "react-native";
import { ProductLinkedTodos_StoreProduct$key } from "./__generated__/ProductLinkedTodos_StoreProduct.graphql";
import { ProductStackScreenProps } from "./ProductStack";
import { ProductLinkedTodosQuery } from "./__generated__/ProductLinkedTodosQuery.graphql";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { AHQueryHandlerLoading, AHSeparator, Box, useTodoSorter } from "arkh";
import { TodoListItem } from "@/src/components/TodoListItem";

type ProductLinkedTodosListRef = {
  onEndReached: () => void;
};
const ProductLinkedTodosList = (props: {
  productId: string;
  product: ProductLinkedTodos_StoreProduct$key;
  openTodo: (id: string) => void;
  ref: React.RefObject<ProductLinkedTodosListRef | null>;
}) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment ProductLinkedTodos_StoreProduct on StoreProduct
      @refetchable(queryName: "ProductLinkedTodosRefetchableQuery") {
        todos(first: $first, after: $after)
          @connection(key: "ProductLinkedTodos_store_todos") {
          edges {
            node {
              id
              ordinal
              ...TodoListItem_todo
            }
          }
        }
      }
    `,
    props.product
  );

  const todoSorter = useTodoSorter();
  const listData = useMemo(() => {
    return data.todos.edges
      ?.filter((edge) => edge?.node)
      .sort((a, b) => todoSorter(a?.node, b?.node));
  }, [data.todos.edges, todoSorter]);

  const todosKey = useMemo(
    () => listData.map(({ node }) => node.id).join(""),
    [listData]
  );

  useLayoutEffect(() => {
    if (Platform.OS === "ios") {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
  }, [todosKey]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <React.Fragment key={item.node.id}>
          {index === 0 ? null : <AHSeparator />}
          <TodoListItem
            key={item.node.id}
            openTodo={(_, todoId) => {
              props.openTodo(todoId);
            }}
            todo={item.node}
          />
        </React.Fragment>
      );
    },
    [props]
  );

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(20);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  useImperativeHandle(props.ref, () => ({
    onEndReached,
  }));

  return (
    <>
      {listData.map((item, index) => renderItem({ item, index }))}
      {isLoadingNext ? (
        <Box spaceVertical="large" key="loading-more">
          <ActivityIndicator />
        </Box>
      ) : null}
    </>
  );
};

const InnerProductLinkedTodos = (
  props: {
    navigation: ProductStackScreenProps<"ProductMain">["navigation"];
    productId: string;
    ref: React.RefObject<ProductLinkedTodosListRef | null>;
  } & { fetchKey: string }
) => {
  const data = useLazyLoadQuery<ProductLinkedTodosQuery>(
    graphql`
      query ProductLinkedTodosQuery($id: ID!, $first: Int!, $after: String) {
        storeProduct(id: $id) {
          id
          ...ProductLinkedTodos_StoreProduct
        }
      }
    `,
    {
      first: 10,
      id: props.productId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const product = data.storeProduct;

  if (!product) {
    return null;
  }

  return (
    <ProductLinkedTodosList
      ref={props.ref}
      productId={product.id}
      openTodo={(id) => {
        props.navigation.navigate("TodoEdit", { todoId: id });
      }}
      product={data.storeProduct}
    />
  );
};

export const ProductLinkedTodos = (props: {
  navigation: ProductStackScreenProps<"ProductMain">["navigation"];
  productId: string;
  ref: React.RefObject<ProductLinkedTodosListRef | null>;
}) => {
  return (
    <LazyQueryNetworkBoundrary
      newFetchKeyOnNavigation
      fallback={
        <Box style={{ flex: 1 }} spaceVertical="medium">
          <AHQueryHandlerLoading />
        </Box>
      }
    >
      {(fetchKey) => (
        <InnerProductLinkedTodos
          ref={props.ref}
          fetchKey={fetchKey}
          productId={props.productId}
          navigation={props.navigation}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
};
